import { Autocomplete, Button, Checkbox, Container, Dialog, DialogTitle, FormControl, Grid, IconButton, Input, InputLabel, MenuItem, Paper, Select, Snackbar, Stack, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow, TextField } from "@mui/material";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { EkasiServeAppBar, equalArrayOfObj, equalObj, formatCurrency, formatNumber, LoadingPage, selectAllOnFocus, SetState, updateObj } from "./Common";
import { Client } from "./Clients";
import { parseSuggestionDescription, searchItemSuggestions } from "./Items";
import { Supplier } from "./Suppliers";
import { Product } from "./Products";
import DeleteIcon from '@mui/icons-material/Delete';
import { UserContext, userIsEmployeeOrAdmin } from "./Users";

export interface Request {
    id: number;
    client_id: number;
    title: string;
    comments: string;
    quote_no: string;
    sent: number;
}

export interface RequestItem {
    id: number;
    request_id: number;
	description: string;
	item_size: string;
	units: number;
	price: number;
    priceText: string;
	vat: number;
	supplier: string;
}

export interface QuoteFormSettings {
    default_supplier: string;
    show_supplier_column: boolean;
}

function defaultItem(id: number, request_id: number, supplier: string): RequestItem {
    return {
        id: id,
        request_id: request_id,
        description: '',
        item_size: '',
        units: 0,
        price: 0,
        priceText: "0",
        vat: 1,
        supplier: supplier,
    }
}

export function mergeRequests(old: Array<Request>, toAdd: Array<Request>): Array<Request> {
    var result = [...old];
    for (let q of toAdd) {
        if (result.find((p) => p.id === q.id) === undefined) {
            result.push(q);
        }
    }
    return result;
}

export function RequestsTable({ requests, setRequests, setSnackbar } : { requests: Array<Request>, setRequests: SetState<Array<Request>>, setSnackbar: SetState<string> } ) {
    const navigate = useNavigate();
    const saveRequest = async (request: Request) => {
        if (!request.client_id) {
            setSnackbar("Client not set");
            return;
        }

        const requestResponse = await fetch("/api/v1/requests", {
            method: (request.id || 0) === 0 ? "POST" : "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({"request": request})
        });
        const requestData = await requestResponse.json();
        var noErrors = true;
        if (requestData.error) {
            noErrors = false;
            setSnackbar(requestData.error);
        }

        if (noErrors) {
            setRequests(requests => requests.map(r => r.id === request.id ? requestData.result : r));
            setSnackbar(request.sent ? "Request Sent" : "Request Recalled");
        }
    }

    const sendRequest = async (request: Request) => {
        request.sent = request.sent === 1 ? 0 : 1;
        await saveRequest(request);
    }

    const auth = useContext(UserContext);

    return (
        <Grid container spacing={2} sx={{ alignItems: "center" }}>
            <Grid size={12}>
                <Paper>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell width="30%">Title</TableCell>
                                    <TableCell width="30%">Comment</TableCell>
                                    <TableCell width="20%">Status</TableCell>
                                    <TableCell width="20%">
                                        <Button variant="contained" component={Link} to="/requests/0" fullWidth>
                                            New Request
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {requests.map((row, idx) => (
                                    <TableRow key={row.id + " - " + idx}>
                                        <TableCell>{row.title}</TableCell>
                                        <TableCell>{row.comments}</TableCell>
                                        <TableCell>
                                            {
                                                row.quote_no
                                                ? <Button variant="contained" component={Link} to={"/quotes/" + encodeURIComponent(row.quote_no)} fullWidth>
                                                    {row.quote_no}
                                                </Button>
                                                : row.sent === 1
                                                ? <Button variant="contained" onClick={() => sendRequest(row)} fullWidth>
                                                    Recall
                                                </Button>
                                                : <Button variant="contained" onClick={() => sendRequest(row)} fullWidth>
                                                    Send
                                                </Button>
                                            }
                                        </TableCell>
                                        <TableCell>
                                            {
                                                row.quote_no
                                                ? <Button variant="contained" onClick={() => navigate("/requests/" + row.id, { state: { "re_request": true } })} fullWidth>
                                                    Request Again
                                                </Button>
                                                : userIsEmployeeOrAdmin(auth)
                                                ? <Button variant="contained" onClick={() => navigate("/quotes/0", { state: { requestIdText: row.id.toString() } })} fullWidth>
                                                    Make Quote
                                                </Button>
                                                : <Button variant="contained" component={Link} to={"/requests/" + row.id} fullWidth>
                                                    Edit
                                                </Button>
                                            }
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>

            </Grid>
        </Grid>
    )
}

export const RequestsPage = () => {
    const [requests, setRequests] = useState<Array<Request>>([]);
    const [offset, setOffset] = useState(0);
    const [snackbar, setSnackbar] = useState("");
    const limit = 20;
    const scrollLimitFound = useRef(false);

    useEffect(() => {
        const handleScroll = () => {
            if (
                window.innerHeight +
                document.documentElement.scrollTop ===
                document.documentElement.offsetHeight
            ) {
                if (!scrollLimitFound.current) {
                    setOffset(oldOffset => oldOffset + limit);
                }
            }
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [limit, scrollLimitFound]);

    useEffect(() => {
        const fetchClients = async () => {
            const response = await fetch(`/api/v1/requests?limit=${limit}&offset=${offset}`);
            const data = await response.json();
            if ((data.result || []).length === 0) {
                scrollLimitFound.current = true;
            }
            setRequests(quotes => mergeRequests(quotes, data.result || []));
            if (data.error) {
                setSnackbar(data.error);
            }
        };
        fetchClients();
    }, [limit, offset]);

    return (
        <Stack spacing={0}>
            <EkasiServeAppBar />
            <Grid component={Container} container spacing={2} sx={{ alignItems: "center" }}>
                <Grid size={12}> <h1> Requests </h1> </Grid>
                <Grid size={12}>
                    <RequestsTable requests={requests} setRequests={setRequests} setSnackbar={setSnackbar}/>
                </Grid>
            </Grid>
            <Snackbar
                open={snackbar !== ""}
                autoHideDuration={6000}
                onClose={(ev, reason) => setSnackbar("")}
                message={snackbar}/>
        </Stack>
    )
}

export function RequestForm({ request, setRequest, clients } : { request: Partial<Request>, setRequest: SetState<Partial<Request>>, clients: Array<Client> }) {
    return (
        <Grid container spacing={2} sx={{ alignItems: "center" }}>
            <Grid size={{ xs: 12, md: 6, lg: 2 }}>
                <FormControl fullWidth>
                    <InputLabel>Clients</InputLabel>
                    <Select
                        label="Client"
                        value={clients.length === 0 ? "" : (request.client_id || "")}
                        onChange={(ev) => setRequest(request => ({ ...request, client_id: ev.target.value as number }))}>
                        {clients.map((client) => (
                            <MenuItem value={client.id}>{client.name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>
            <Grid size={{ xs: 6, md: 3, lg: 4 }}>
                <TextField
                    id="quote_title"
                    label="Title"
                    variant="outlined"
                    onFocus={selectAllOnFocus}
                    onChange={(e) => setRequest(request => ({ ...request, title: e.target.value }))}
                    fullWidth
                    value={request.title || ""} />
            </Grid>
            <Grid size={{ xs: 6, md: 3, lg: 6 }}>
                <TextField
                    id="comment"
                    label="Comments"
                    variant="outlined"
                    onFocus={selectAllOnFocus}
                    onChange={(e) => setRequest(request => ({ ...request, comments: e.target.value }))}
                    fullWidth
                    value={request.comments || ""} />
            </Grid>
        </Grid>
    )
}

export function mapDbRequestItemsToLocal(item: RequestItem, index: number): RequestItem {
    return {
        ...item,
        id: index,

        priceText: formatNumber(item.price),
    };
}

export function ItemRow({ item, setItems, suppliers }: { item: RequestItem, setItems: SetState<Array<RequestItem>>, suppliers: Array<Supplier> }) {
    const [suggestions, setSuggestions] = useState<Array<Product | any>>([]);

    const [itemDescription, setItemDescription] = useState("");
    useEffect(() => {
        const timeOut = setTimeout(async () => 
            setSuggestions(await searchItemSuggestions(itemDescription, suppliers, { default_supplier: "Makro", show_supplier_column: true })), 1000
        );
        return () => clearTimeout(timeOut);
    }, [itemDescription, suppliers]);

    return (
        <TableRow>
            <TableCell>
                <Input
                    fullWidth
                    onFocus={selectAllOnFocus}
                    value={item.supplier || ""}
                    onChange={ev => setItems(items => items.map(it => updateObj(it, {id: item.id, supplier: ev.target.value})))}/>
            </ TableCell>
            <TableCell>
                <Autocomplete 
                    fullWidth
                    freeSolo
                    inputValue={item.description || ""}
                    onFocus={selectAllOnFocus}
                    onInputChange={
                        async (_, value) => {
                            const desc = parseSuggestionDescription(value);
                            setItems(items => items.map(it => updateObj(it, {id: item.id, description: desc})));
                            setItemDescription(desc);
                        }
                    }
                    onChange={
                        (_, value) => {
                            if (typeof value === "object") {
                                setItems(items => items.map(it => updateObj(it, {
                                    id: item.id,
                                    description: value?.description || "",
                                    item_size: value?.item_size || "",
                                    cost: value?.cost || 0,
                                    costText: formatNumber(value?.cost),
                                    price: value?.price || 0,
                                    priceText: formatNumber(value?.price),
                                    supplier: value?.supplier_text,
                                    vat: value?.vat || 0
                                })));
                            }
                        }
                    }
                    renderInput={(params) => <TextField {...params} variant="standard" fullWidth/>}
                    options={suggestions}
                />
            </TableCell>
            <TableCell>
                <Input 
                    fullWidth
                    onFocus={selectAllOnFocus}
                    value={item.item_size || ""}
                    onChange={ev => setItems(items => items.map(it => updateObj(it, {id: item.id, item_size: ev.target.value})))}
                />
            </TableCell>
            <TableCell>
                <Input 
                    fullWidth
                    onFocus={selectAllOnFocus}
                    value={item.units || ""}
                    onChange={ev => setItems(items => items.map(it => updateObj(it, {id: item.id, units: parseInt(ev.target.value)})))}
                />
            </TableCell>
            <TableCell>
                <Checkbox 
                    checked={item.vat === 1}
                    onChange={(_, checked) => setItems(items => items.map(it => updateObj(it, {id: item.id, vat: checked ? 1 : 0})))}/>
            </TableCell>
            <TableCell>
                {formatCurrency(item.price || 0)}
            </TableCell>
            <TableCell>
                {formatCurrency((item.price || 0) * (item.units || 0))}
            </TableCell>
            <TableCell align="center" onClick={ev => setItems(items => items.filter(it => it.id !== item.id))}>
                <IconButton color="error">
                    <DeleteIcon />
                </IconButton>
            </TableCell>
        </TableRow>
    )
}

export function ItemsTable({ request, items, setItems, suppliers }: { request: Partial<Request>, items: Array<RequestItem>, setItems: SetState<Array<RequestItem>>, suppliers: Array<Supplier> }) {
    return (
        <Paper>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell width="10%">Supplier</TableCell>
                        <TableCell width="30%">Description</TableCell>
                        <TableCell width="15%">Item Size</TableCell>
                        <TableCell width="10%">Units</TableCell>
                        <TableCell width="2.5%">VAT</TableCell>
                        <TableCell width="15%">Est. Price</TableCell>
                        <TableCell width="15%">Est. Total</TableCell>
                        <TableCell width="2.5%"></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {
                        items.map(item => (<ItemRow key={item.id} item={item} setItems={setItems} suppliers={suppliers}/>))
                    }
                </TableBody>
                <TableFooter>
                    <TableRow>
                        <TableCell colSpan={2} align="center">
                            <Button fullWidth variant="contained" color="primary" onClick={() => setItems(old => [...old, defaultItem(old.length, request.id || 0, "Makro")])}>
                                Add Item
                            </Button>
                        </TableCell>
                        <TableCell></TableCell>
                        <TableCell></TableCell>
                        <TableCell></TableCell>
                        <TableCell></TableCell>
                        <TableCell></TableCell>
                        <TableCell></TableCell>
                    </TableRow>
                </TableFooter>
            </Table>
        </Paper>
    )
}

export const RequestPage = () => {
    const { requestIdText } = useParams();
    const requestId = requestIdText === undefined || requestIdText === "0" ? 0 : parseInt(requestIdText);

    const { state } = useLocation();

    const [clients, setClients] = useState<Array<Client>>([]);

    const [request, setRequest] = useState<Partial<Request>>({});
    const [items, setItems] = useState<Array<RequestItem>>([]);
    const [suppliers, setSuppliers] = useState<Array<Supplier>>([]);
    const [savedRequest, setSavedRequest] = useState<Partial<Request>>({});
    const [savedItems, setSavedItems] = useState<Array<RequestItem>>([]);
    const [edited, setEdited] = useState(false);
    const [isLoading, setIsLoading] = useState(0);

    const [snackbar, setSnackbar] = useState("");
    const [requestDelete, setRequestDelete] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            if (requestId !== 0) {
                const response = await fetch("/api/v1/requests/" + requestId);
                const data = await response.json();
                if (data.error) {
                    setSnackbar(data.error);
                } else {
                    if (state?.re_request === true) {
                        data.result.request.sent = 0;
                        data.result.request.quote_no = "";
                    }
                    setRequest(data.result.request || {});
                    setSavedRequest(data.result.request || {});
                }
            }
            setIsLoading(lock => lock - 1);
        }
        setIsLoading(lock => lock + 1);
        fetchData();
    }, [requestId, state]);

    useEffect(() => {
        const fetchData = async () => {
            const response = await fetch("/api/v1/clients");
            const data = await response.json();
            setClients(data.result || []);
            if (data.error) {
                setSnackbar(data.error);
            }
            setIsLoading(lock => lock - 1);
        }
        setIsLoading(lock => lock + 1);
        fetchData();
    }, []);

    // Update Products List
    useEffect(() => {
        const fetchProducts = async () => {
            const response = await fetch("/api/v1/suppliers");
            const data = await response.json();
            setSuppliers(data.result || []);
            if (data.error) {
                setSnackbar(data.error);
            }
            setIsLoading(lock => lock - 1);
        };
        setIsLoading(lock => lock + 1);
        fetchProducts();
    }, []);

    // Update Request Items
    useEffect(() => {
        const fetchData = async () => {
            if (requestId !== 0) {
                const response = await fetch("/api/v1/request-items/" + requestId);
                const data = await response.json();
                setItems(data?.result?.map(mapDbRequestItemsToLocal) || []);
                setSavedItems(data?.result?.map(mapDbRequestItemsToLocal) || []);
                if (data.error) {
                    setSnackbar(data.error);
                }
            }
            setIsLoading(lock => lock - 1);
        }
        setIsLoading(lock => lock + 1);
        fetchData();
    }, [requestId]);

    useEffect(() => {
        setEdited(!(equalObj(request, savedRequest) && equalArrayOfObj(items, savedItems)));
    }, [request, savedRequest, items, savedItems]);

    const saveRequest = useCallback(async () => {
        if (!request.client_id) {
            setSnackbar("Client not set");
            return;
        }

        const requestResponse = await fetch("/api/v1/requests", {
            method: (request.id || 0) === 0 ? "POST" : "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({"request": request, "items": items, "set_items": true})
        });
        const requestData = await requestResponse.json();
        var noErrors = true;
        if (requestData.error) {
            noErrors = false;
            setSnackbar(requestData.error);
        } else {
            setRequest(requestData.result || {});
            setSavedRequest(requestData.result || {});
            setItems(items);
            setSavedItems(items);
        }

        if (noErrors) {
            if (requestIdText === "0") {
                navigate("/requests/" + requestData.result.id, { replace: true });
            }
            setSnackbar("Information Saved");
        }
    }, [items, request, navigate, requestIdText]);

    const sendRequest = useCallback(async () => {
        request.sent = request.sent === 1 ? 0 : 1;
        await saveRequest();
    }, [request, saveRequest]);

    const deleteRequest = useCallback(async () => {
        const requestResponse = await fetch("/api/v1/requests/" + (request.id || 0), { method: "DELETE" });
        const requestData = await requestResponse.json();
        var noError = true;
        if (requestData.error) {
            setSnackbar(requestData.error);
            noError = false;
        }

        const itemsResponse = await fetch("/api/v1/request-items/" + (request.id || 0), { method: "DELETE" });
        const itemsData = await itemsResponse.json();
        if (itemsData.error) {
            setSnackbar(itemsData.error);
            noError = false;
        }

        if (noError) {
            setRequest(requestData.result || {});
            setItems(itemsData?.result?.map(mapDbRequestItemsToLocal) || []);
            navigate(-1);
        }
    }, [navigate, request.id]);

    if (isLoading) return (<LoadingPage />);

    return (
        <Stack spacing={0}>
            <EkasiServeAppBar />
            <Grid component={Container} container spacing={2} sx={{ alignItems: "center" }}>
                <Grid size={{ xs: 12, md: 12, lg: 6 }}> <h1> Request </h1> </Grid>
                {
                requestId === 0 || request.sent === 0
                    ? <Grid size={{ xs: 6, md: 4, lg: 2}}>
                        <Button variant="contained" onClick={sendRequest} fullWidth> Send </Button>
                    </Grid>
                    : <Grid size={{ xs: 6, md: 4, lg: 2}}>
                        <Button variant="contained" onClick={sendRequest} fullWidth> Recall </Button>
                    </Grid>
                }
                <Grid size={{ xs: 6, md: 4, lg: 2}}>
                    <Button disabled={!edited} variant="contained" onClick={saveRequest} fullWidth> Save </Button>
                </Grid>
                {
                requestId === 0 
                    ? <Grid size={{ xs: 6, md: 4, lg: 2}}>
                        <Button variant="contained" onClick={() => navigate(-1)} fullWidth> Cancel </Button>
                    </Grid>
                    : <Grid size={{ xs: 6, md: 4, lg: 2}}>
                        <Button variant="contained" onClick={() => setRequestDelete(true)} fullWidth color="error"> Delete </Button>
                    </Grid>
                }
                <Grid size={12}>
                    <RequestForm
                        request={request}
                        setRequest={setRequest}
                        clients={clients}/>
                </Grid>
            </Grid>
            <Grid size={12} sx={{ paddingTop: 2 }}> </Grid>
            <Grid component={Container} container spacing={2}>
                <Grid size={12}>
                    <ItemsTable request={request} items={items} setItems={setItems} suppliers={suppliers}/>
                </Grid>
            </Grid>

            <Snackbar
                open={snackbar !== ""}
                autoHideDuration={6000}
                onClose={(ev, reason) => setSnackbar("")}
                message={snackbar}/>
            <Dialog open={requestDelete}>
                <DialogTitle> Are you sure you want to delete this quote? </DialogTitle>
                <Button onClick={deleteRequest}> Yes, Delete </Button>
                <Button onClick={() => setRequestDelete(false)}> No, Cancel </Button>
            </Dialog>
        </Stack>
    )
}